/**
 * Drawing a score, and finding the document again in what was drawn.
 *
 * The engine is given MusicXML rather than the document itself, because MusicXML is the one
 * dialect every notation renderer already reads and going through it costs a few
 * milliseconds against writing and maintaining an adapter per engine. The export is the
 * same one a user saves, with one difference: the document's ids are stamped into it, so
 * that the drawn page can be mapped back to the nodes it came from.
 *
 * The page it is drawn on is the document's own: {@link layoutOptions} turns the score's
 * layout into the engine's options, and whatever the caller passes is laid over that.
 *
 * A rendering is a value, not an object with a lifecycle. Re-rendering after an edit
 * produces a new one; nothing here mutates the previous. That matches the document, which
 * is immutable for the same reason, and it means a stale rendering held by a slow consumer
 * is merely out of date rather than corrupt.
 */

import type { EventId, MeasureId } from "../document/ids";
import type { Score } from "../document/types";
import { toMusicXml } from "../musicxml/export";
import { asSystemBreaks, breakMode, breaksIn, systemStarts, withSystemBreaks } from "./breaks";
import { elementEventId, elementMeasureId, idsWithin } from "./ids";
import { nudged, offsetsOf } from "./nudge";
import { optimized } from "./condense";
import { ossiaPlan, withOssia } from "./ossia";
import { engineOptions, layoutOptions, unitOf } from "./port";
import type { NotationEngine, RenderOptions } from "./port";

/** One drawn page, as the engine's own SVG markup. */
export interface RenderedPage {
  /** 1-based, as the engine numbers them. */
  readonly index: number;
  readonly svg: string;
}

/** A score as it was drawn, with the map from the document to the drawn elements. */
export interface Rendering {
  readonly pages: readonly RenderedPage[];
  /** The MusicXML the engine was given, kept so a failed render can be inspected. */
  readonly source: string;
  /** Page each event was drawn on. An event the engine dropped is absent. */
  readonly eventPage: ReadonlyMap<EventId, number>;
  readonly measurePage: ReadonlyMap<MeasureId, number>;
  /**
   * Drawn element ids per event, in the order the engine wrote them.
   *
   * A chord has one per note head, so an overlay that wants to outline the whole chord has
   * all of its parts here rather than only the head the click landed on.
   */
  readonly eventElements: ReadonlyMap<EventId, readonly string[]>;
}

/** Raised when the engine refuses a document it was handed. */
export class RenderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RenderError";
  }
}

/**
 * Draw a score.
 *
 * The engine is loaded and rendered in one call rather than kept warm between edits. An
 * engine holds one document at a time, so a caller that renders two scores side by side
 * needs two engines anyway, and reloading is what makes the result a plain value.
 */
export function renderScore(
  engine: NotationEngine,
  score: Score,
  options: RenderOptions = {},
): Rendering {
  // Beams are derived here and nowhere else: the engine draws what the file says, and a
  // note left to the engraver says nothing. See `musicxml/export-beams.ts`.
  const breaks = breaksIn(score);
  // A page break is drawn in a second pass, and what the first pass is for is the systems:
  // it is given the page breaks as system breaks so that the system before one ends there.
  let source = toMusicXml(breaks.page ? asSystemBreaks(score) : score, { ids: true, beams: true });
  // The document's own page comes first and the caller's options over it, so that a host
  // can zoom or force a break without having to restate the paper it is drawing on. How
  // the breaks the score encodes are obeyed is worked out rather than defaulted, because
  // an engine left on its own terms throws a short system away. See `render/breaks.ts`.
  const settings = { ...layoutOptions(score.layout), breaks: breakMode(breaks), ...options };
  engine.setOptions(engineOptions(settings));
  if (!engine.loadData(source)) {
    throw new RenderError("the rendering engine rejected the score");
  }
  // A page break is obeyed in one mode only, and that mode lays out nothing by itself. So
  // the layout just settled on is read back as breaks of its own and stated in full, and
  // the score is drawn again under them, cut into pages where it was asked to be.
  if (breaks.page && settings.breaks === "smart" && engine.getMEI !== undefined) {
    const staged = withSystemBreaks(score, systemStarts(engine.getMEI({ scoreBased: false })));
    source = toMusicXml(staged, { ids: true, beams: true });
    engine.setOptions(engineOptions({ ...settings, breaks: "encoded" }));
    if (!engine.loadData(source)) {
      throw new RenderError("the rendering engine rejected the score it had laid out");
    }
  }
  // Two things are asked for in MEI and nowhere else: leaving the silent staves out, and
  // drawing an ossia as an ossia. A score that wants either is loaded a second time from
  // the engine's own MEI. See `render/condense.ts` and `render/ossia.ts`.
  const ossias = ossiaPlan(score);
  const condensing = settings.condense !== undefined && settings.condense !== "none";
  if (condensing || ossias.staves.size > 0) {
    const mei = engine.getMEI === undefined ? null : engine.getMEI({});
    const second = mei === null ? null : withOssia(mei, ossias);
    if (second !== null && !engine.loadData(condensing ? optimized(second) : second)) {
      throw new RenderError("the rendering engine rejected its own score");
    }
  }

  const pages: RenderedPage[] = [];
  const eventPage = new Map<EventId, number>();
  const measurePage = new Map<MeasureId, number>();
  const eventElements = new Map<EventId, string[]>();

  // Elements moved by hand are moved on the way out rather than by the engine, which
  // ignores the file's own offsets. See `render/nudge.ts`.
  const offsets = offsetsOf(score);
  const unit = unitOf(settings);

  const count = engine.getPageCount();
  for (let index = 1; index <= count; index += 1) {
    const svg = nudged(engine.renderToSVG(index), offsets, unit);
    pages.push({ index, svg });
    for (const elementId of idsWithin(svg)) {
      const measure = elementMeasureId(elementId);
      if (measure !== null) {
        if (!measurePage.has(measure)) measurePage.set(measure, index);
        continue;
      }
      const event = elementEventId(elementId);
      if (event === null) continue;
      if (!eventPage.has(event)) eventPage.set(event, index);
      const drawn = eventElements.get(event);
      if (drawn === undefined) eventElements.set(event, [elementId]);
      else drawn.push(elementId);
    }
  }

  return { pages, source, eventPage, measurePage, eventElements };
}

/** What is sounding at a moment, as document ids rather than drawn ones. */
export interface Sounding {
  readonly page: number;
  readonly events: readonly EventId[];
  readonly measure: MeasureId | null;
}

/**
 * The events sounding at a moment of playback.
 *
 * Notes and rests are answered together and chords are left out: the engine reports a chord
 * both as its own element and as the note heads inside it, and the document has no node for
 * the former, so counting it as well would name the same event twice.
 */
export function soundingAt(engine: NotationEngine, milliseconds: number): Sounding {
  const at = engine.getElementsAtTime(milliseconds);
  const events: EventId[] = [];
  const seen = new Set<EventId>();
  for (const elementId of [...at.notes, ...at.rests]) {
    const event = elementEventId(elementId);
    if (event === null || seen.has(event)) continue;
    seen.add(event);
    events.push(event);
  }
  return { page: at.page, events, measure: elementMeasureId(at.measure) };
}

/** When an event sounds, in milliseconds from the start, or `null` if it was not drawn. */
export function timeOfEvent(
  engine: NotationEngine,
  rendering: Rendering,
  id: EventId,
): number | null {
  const drawn = rendering.eventElements.get(id)?.[0];
  if (drawn === undefined) return null;
  const at = engine.getTimeForElement(drawn);
  return Number.isFinite(at) ? at : null;
}
